// Shared helper used by BOTH the sticky-bar custom element and the gallery IIFE
// below. Declared at module scope so the sticky bar (which is not inside the
// IIFE) can call it — previously it was only defined inside the IIFE, so the
// sticky bar threw a ReferenceError that aborted connectedCallback before its
// variant/color picker listeners were attached.
function getSelectedVariantFromPicker(productInfo) {
  const json = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent;
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

if (!customElements.get('product-hero-sticky-bar')) {
  customElements.define('product-hero-sticky-bar', class extends HTMLElement {
    connectedCallback() {
      const formId = this.dataset.formId;
      const form = document.getElementById(formId);
      if (!form) return;

      const cta = this.querySelector('.product-hero__sticky-cta');
      const productInfo = form.closest('product-info');
      const sectionId = productInfo?.dataset.section;

      const getSubmitBtn = () => form.querySelector('[type="submit"].product-form__submit');

      const syncCta = () => {
        if (!cta) return;
        const submitBtn = getSubmitBtn();
        if (!submitBtn) return;

        const labelSpan = submitBtn.querySelector('span:not(.loading__spinner)');
        const label = labelSpan ? labelSpan.textContent.trim() : '';
        const primaryEl = cta.querySelector('[data-sticky-cta-primary]');
        const secondaryEl = cta.querySelector('[data-sticky-cta-secondary]');

        if (label && primaryEl) {
          const parts = label.split(/\s[—-]\s/);
          primaryEl.textContent = parts[0].trim();
          if (parts.length > 1) {
            if (secondaryEl) {
              secondaryEl.textContent = parts.slice(1).join(' - ').trim();
              secondaryEl.hidden = false;
            }
          } else if (secondaryEl) {
            secondaryEl.textContent = '';
            secondaryEl.hidden = true;
          }
        }

        const isDisabled = submitBtn.disabled || submitBtn.hasAttribute('aria-disabled');
        cta.disabled = isDisabled;
        if (isDisabled) {
          cta.setAttribute('aria-disabled', 'true');
        } else {
          cta.removeAttribute('aria-disabled');
        }
      };

      const syncStickyDetails = () => {
        if (!productInfo || !sectionId) return;

        const metaEl = this.querySelector('[data-sticky-meta]');
        const priceEl = this.querySelector('[data-sticky-price]');
        const thumbEl = this.querySelector('[data-sticky-thumb]');
        const variant = getSelectedVariantFromPicker(productInfo);

        if (metaEl && variant) {
          const parts = [];
          const packLabel = variant.title || variant.options?.[0];
          if (packLabel) parts.push(packLabel);
          if (this.dataset.showRating === 'true' && this.dataset.ratingValue) {
            parts.push(`${this.dataset.ratingValue} ★`);
          }
          metaEl.textContent = parts.join(' · ');
        }

        if (priceEl) {
          const priceBlock = productInfo.querySelector(`#price-${sectionId}`);
          const priceSource =
            priceBlock?.querySelector('.price-item--sale') ||
            priceBlock?.querySelector('.price-item--regular');
          if (priceSource) {
            priceEl.textContent = priceSource.textContent.trim();
          }
        }

        if (thumbEl && variant?.featured_image?.src) {
          const src = variant.featured_image.src;
          const separator = src.includes('?') ? '&' : '?';
          thumbEl.src = `${src}${separator}width=120`;
          if (variant.featured_image.alt) {
            thumbEl.alt = variant.featured_image.alt;
          }
        }
      };

      const update = () => {
        const scrolledPast = form.getBoundingClientRect().bottom < 0;
        if (scrolledPast) {
          this.setAttribute('data-visible', '');
          this.setAttribute('aria-hidden', 'false');
        } else {
          this.removeAttribute('data-visible');
          this.setAttribute('aria-hidden', 'true');
        }
      };

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          update();
          ticking = false;
        });
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();

      // Wire the sticky CTA to submit the real product form so Dawn's AJAX
      // add-to-cart flow fires (cart drawer, variant, quantity) — same as
      // clicking the in-page Add to Cart button directly.
      if (cta) {
        cta.addEventListener('click', (e) => {
          e.preventDefault();
          const submitBtn = getSubmitBtn();
          if (submitBtn && !submitBtn.disabled) {
            submitBtn.click();
          }
        });
      }

      syncCta();
      syncStickyDetails();

      if (typeof window.subscribe === 'function' && sectionId) {
        window.subscribe('variant-change', (payload) => {
          if (payload?.data?.sectionId !== sectionId) return;
          requestAnimationFrame(() => {
            syncCta();
            syncStickyDetails();
          });
        });
      }

      // Sticky color picker: click a color → mirror to main picker so Dawn's
      // variant-update flow runs. Sync sticky display when variant changes
      // from anywhere (sticky picker OR main picker).
      const details = this.querySelector('[data-sticky-color]');
      if (details) {
        details.addEventListener('click', (e) => {
          const btn = e.target.closest('.product-hero__sticky-color-option');
          if (!btn) return;
          const valueId = btn.dataset.optionValueId;
          const radio = document.querySelector(
            `input[type="radio"][data-option-value-id="${valueId}"]`
          );
          if (!radio) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          details.open = false;
        });

        const colorPos = parseInt(productInfo?.dataset.colorOptionPosition || '0', 10);

        const syncDisplay = () => {
          if (!productInfo || !colorPos) return;
          const groups = productInfo.querySelectorAll('variant-selects .product-form__input');
          const colorGroup = groups[colorPos - 1];
          const checked = colorGroup?.querySelector('input[type="radio"]:checked');
          if (!checked) return;

          const valueId = checked.dataset.optionValueId;
          const matchingOption = details.querySelector(
            `.product-hero__sticky-color-option[data-option-value-id="${valueId}"]`
          );
          const swatch = details.querySelector('[data-active-swatch]');
          const label = details.querySelector('[data-active-label]');

          if (swatch && matchingOption) {
            const hex = getComputedStyle(matchingOption).getPropertyValue('--sticky-color').trim();
            swatch.style.setProperty('--sticky-color', hex);
          }
          if (label) label.textContent = checked.value;

          details.querySelectorAll('.product-hero__sticky-color-option').forEach((opt) => {
            opt.setAttribute(
              'aria-pressed',
              opt.dataset.optionValueId === valueId ? 'true' : 'false'
            );
          });
        };

        if (typeof window.subscribe === 'function') {
          window.subscribe('variant-change', (payload) => {
            if (payload?.data?.sectionId !== sectionId) return;
            requestAnimationFrame(syncDisplay);
          });
        }

        document.addEventListener('click', (e) => {
          if (details.open && !details.contains(e.target)) {
            details.open = false;
          }
        });
      }

      // Sticky variant picker: click an option → mirror to main card/button picker.
      // Match the target radio by option POSITION + value STRING rather than
      // data-option-value-id, since product_option_value.id can be blank on some
      // products — which would otherwise resolve every option to the same radio.
      const variantDetails = this.querySelectorAll('[data-sticky-variant]');
      if (variantDetails.length) {
        const norm = (s) => (s || '').trim().toLowerCase();

        // Keep in sync with snippets/bundle-unit-label.liquid: "3" + "Mask" -> "3 Masks".
        // No unit noun configured (the default for most products) returns the raw value.
        const unitLabel = (value, unit) => {
          if (!unit) return value;
          const count = parseFloat(value);
          return `${value} ${unit}${count > 1 ? 's' : ''}`;
        };

        const groupForPosition = (position) => {
          if (!productInfo) return null;
          const groups = productInfo.querySelectorAll('variant-selects .product-form__input');
          return groups[position - 1] || null;
        };

        const radioForValue = (group, value, valueId) => {
          if (!group) return null;
          const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
          // Prefer value-id match when ids are present and unique within the group.
          if (valueId) {
            const byId = radios.find((r) => r.dataset.optionValueId === valueId);
            if (byId) return byId;
          }
          return radios.find((r) => norm(r.value) === norm(value)) || null;
        };

        variantDetails.forEach((vDetails) => {
          const optionPosition = parseInt(vDetails.dataset.optionPosition || '1', 10);

          vDetails.addEventListener('click', (e) => {
            const btn = e.target.closest('.product-hero__sticky-variant-option');
            if (!btn) return;

            // Stop the click from bubbling to document-level handlers (e.g. theme
            // search-modal closer that calls .close() on a null dialog).
            e.stopPropagation();

            // Close the panel first, regardless of whether the radio is found.
            vDetails.open = false;

            const group = groupForPosition(optionPosition);
            const radio = radioForValue(group, btn.dataset.optionValue, btn.dataset.optionValueId);
            if (!radio || radio.disabled) return;

            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          });

          const syncVariantDisplay = () => {
            const group = groupForPosition(optionPosition);
            const checked = group && group.querySelector('input[type="radio"]:checked');
            if (!checked) return;

            const selectedValue = norm(checked.value);
            const lbl = vDetails.querySelector('[data-active-variant-label]');
            // Mirrors snippets/bundle-unit-label.liquid so the label keeps its unit
            // noun ("3 Masks") after a variant change instead of falling back to "3".
            if (lbl) lbl.textContent = unitLabel(checked.value, vDetails.dataset.unitNoun);

            vDetails.querySelectorAll('.product-hero__sticky-variant-option').forEach((opt) => {
              opt.setAttribute(
                'aria-pressed',
                norm(opt.dataset.optionValue) === selectedValue ? 'true' : 'false'
              );
            });
          };

          if (typeof window.subscribe === 'function' && sectionId) {
            window.subscribe('variant-change', (payload) => {
              if (payload && payload.data && payload.data.sectionId !== sectionId) return;
              requestAnimationFrame(syncVariantDisplay);
            });
          }

          // Sync label on load in case JS auto-selected a different variant.
          requestAnimationFrame(syncVariantDisplay);
        });

        // Single document listener to close any open variant panel on outside click.
        document.addEventListener('click', (e) => {
          variantDetails.forEach((vd) => {
            if (vd.open && !vd.contains(e.target)) {
              vd.open = false;
            }
          });
        });
      }
    }
  });
}

// Variant-aware gallery enhancement.
//
// Groups gallery items by the color option's values via filename detection
// (e.g. "Akina_-_Sconces_-_Black_-_3.jpg"). On variant change, re-sorts the
// gallery (Dawn's product-info.js prepends the active item, breaking our
// grouping) and centers the matching thumbnail in the thumbnail strip.
//
// On desktop, also wires the main image prev/next caret buttons and arrow
// keys to navigate between media items. The default slider-component scrolls
// items horizontally on mobile via swipe; on desktop our CSS hides non-active
// items, so the buttons would otherwise be no-ops.
//
// The Liquid section emits `data-color-option-position` on <product-info>
// indicating which 1-based product option holds colors. If 0, all the
// variant-aware behavior is skipped and only desktop nav is enabled.
(function initProductHeroGallery() {
  const desktopMQL = window.matchMedia('(min-width: 750px)');

  function filenameOf(img) {
    const src = (img && (img.currentSrc || img.src || img.dataset.src)) || '';
    return src.split('/').pop().split('?')[0].replace(/\.[^.]+$/, '').toUpperCase();
  }

  function getViewerList(gallery) {
    return gallery.querySelector('[id^="GalleryViewer"] ul');
  }

  function getThumbList(gallery) {
    return gallery.querySelector('[id^="GalleryThumbnails"] ul');
  }

  function getActiveItem(gallery) {
    return gallery.querySelector('[id^="GalleryViewer"] li[data-media-id].is-active');
  }

  function getColorInputs(productInfo) {
    const pos = parseInt(productInfo.dataset.colorOptionPosition || '0', 10);
    if (!pos) return null;
    const groups = productInfo.querySelectorAll('.product-form__input');
    const colorGroup = groups[pos - 1];
    if (!colorGroup) return null;
    const inputs = Array.from(colorGroup.querySelectorAll('input[type="radio"]'));
    return inputs.length ? inputs : null;
  }

  function colorRankFor(item, colorOrder) {
    const f = filenameOf(item.querySelector('img'));
    return colorOrder.findIndex(c => f.includes(c));
  }

  function sortGalleryByColor(gallery, colorOrder) {
    const viewerList = getViewerList(gallery);
    if (!viewerList) return;

    const items = Array.from(viewerList.children).filter(el => el.dataset.mediaId);
    const untagged = items.filter(item => colorRankFor(item, colorOrder) === -1);
    const tagged = items.filter(item => colorRankFor(item, colorOrder) !== -1);
    tagged.sort((a, b) => colorRankFor(a, colorOrder) - colorRankFor(b, colorOrder));
    const newOrder = [...untagged, ...tagged];
    newOrder.forEach(item => viewerList.appendChild(item));

    const thumbList = getThumbList(gallery);
    if (thumbList) {
      const orderMap = new Map(newOrder.map((item, i) => [item.dataset.mediaId, i]));
      const thumbItems = Array.from(thumbList.children).filter(el => el.dataset.target);
      thumbItems
        .sort((a, b) => (orderMap.get(a.dataset.target) ?? 9999) - (orderMap.get(b.dataset.target) ?? 9999))
        .forEach(item => thumbList.appendChild(item));
    }
  }

  function centerThumbnailFor(gallery, mediaId) {
    const thumbList = getThumbList(gallery);
    if (!thumbList) return;
    const thumb = thumbList.querySelector(`[data-target="${mediaId}"]`);
    if (!thumb) return;

    const target = thumb.offsetLeft - (thumbList.clientWidth - thumb.clientWidth) / 2;
    thumbList.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }

  function updateSliderCounter(gallery) {
    const viewerList = getViewerList(gallery);
    if (!viewerList) return;
    const items = Array.from(viewerList.children).filter(el => el.dataset.mediaId);
    const activeIdx = items.findIndex(el => el.classList.contains('is-active'));
    const counter = gallery.querySelector('.slider-counter--current');
    const total = gallery.querySelector('.slider-counter--total');
    if (counter && activeIdx !== -1) counter.textContent = activeIdx + 1;
    if (total) total.textContent = items.length;
  }

  function navigate(gallery, direction) {
    const viewerList = getViewerList(gallery);
    if (!viewerList) return;
    const items = Array.from(viewerList.children).filter(el => el.dataset.mediaId);
    const idx = items.findIndex(el => el.classList.contains('is-active'));
    if (idx === -1) return;
    const nextIdx = (idx + direction + items.length) % items.length;
    const nextItem = items[nextIdx];
    if (!nextItem) return;
    gallery.setActiveMedia?.(nextItem.dataset.mediaId, false);
    centerThumbnailFor(gallery, nextItem.dataset.mediaId);
    updateSliderCounter(gallery);
  }

  function setupDesktopNav(gallery) {
    const prev = gallery.querySelector('[id^="GalleryViewer"] .slider-button--prev');
    const next = gallery.querySelector('[id^="GalleryViewer"] .slider-button--next');
    if (!prev || !next) return;

    // Intercept clicks before slider-component handles them; on desktop our CSS
    // hides non-active items so its scroll-based navigation is a no-op.
    const handle = (direction) => (e) => {
      if (!desktopMQL.matches) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      navigate(gallery, direction);
    };
    prev.addEventListener('click', handle(-1), true);
    next.addEventListener('click', handle(1), true);

    gallery.setAttribute('tabindex', '0');
    gallery.addEventListener('keydown', (e) => {
      if (!desktopMQL.matches) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); navigate(gallery, -1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); navigate(gallery, 1); }
    });

    // Keep counter synced when other code (e.g. thumbnail clicks) toggles is-active.
    const viewerList = getViewerList(gallery);
    if (viewerList && window.MutationObserver) {
      const mo = new MutationObserver(() => updateSliderCounter(gallery));
      mo.observe(viewerList, { subtree: true, attributes: true, attributeFilter: ['class'] });
    }
  }

  function updateColorLegend(productInfo) {
    const legendValues = productInfo.querySelector('.product-hero__color-legend-values');
    if (!legendValues) return;
    // Query inputs live — Dawn re-renders the variant-picker HTML on each
    // variant change, so any cached input refs from init() go stale.
    const inputs = getColorInputs(productInfo);
    const checked = inputs?.find(i => i.checked);
    if (checked) {
      legendValues.textContent = checked.value;
    } else {
      // Fallback: show full list when no variant is selected.
      legendValues.textContent = legendValues.dataset.colorValuesAll || '';
    }
  }

  function reorderMobileColorControls(productInfo) {
    const info = productInfo.querySelector('.product-hero__info');
    if (!info) return;

    // Anchor on the price BLOCK, not the inner price row. product-info.js does
    // `destination.innerHTML = source.innerHTML` on #price-<section> for every
    // variant change, so anything relocated inside that block is destroyed by the
    // first switch — which silently killed the variant picker, and with it the
    // bundle selector, after a single tap. This path is mobile-only, so the bug was too.
    const price = info.querySelector('.product-hero__price-block') || info.querySelector('.product-hero__price');
    const legend = info.querySelector('.product-hero__color-legend');
    const variantPicker = info.querySelector('variant-selects');
    if (!price || !variantPicker) return;

    let anchor = info.querySelector('[data-mobile-color-anchor]');
    if (!anchor) {
      anchor = document.createElement('span');
      anchor.hidden = true;
      anchor.setAttribute('data-mobile-color-anchor', '');
      (legend || variantPicker).before(anchor);
    }

    if (!desktopMQL.matches) {
      // Mobile: place color controls right after price row.
      if (legend) {
        price.after(legend);
        legend.after(variantPicker);
      } else {
        price.after(variantPicker);
      }
      return;
    }

    // Desktop: restore original order at anchor position.
    if (legend) {
      anchor.after(legend);
      legend.after(variantPicker);
    } else {
      anchor.after(variantPicker);
    }
  }

  function syncCardSelection(productInfo) {
    const target = (productInfo.dataset.mostPopularVariant || '').trim().toLowerCase();
    if (!target) return;

    productInfo.querySelectorAll('[data-variant-card]').forEach((card) => {
      const radio = card.querySelector('.product-form__card-radio');
      if (!radio) return;
      const isMatch = radio.value.toLowerCase().trim() === target;
      card.classList.toggle('is-selected', radio.checked);
      if (isMatch && radio.checked) card.classList.add('is-selected');
    });
  }

  function selectMostPopularVariant(productInfo) {
    const target = (productInfo.dataset.mostPopularVariant || '').trim().toLowerCase();
    if (!target) return false;

    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) return false;

    // Card picker radios carry .product-form__card-radio; the button/pill picker
    // renders plain radios via product-variant-options.liquid. Both must be
    // considered, otherwise on a pill-picker product the Liquid-side
    // auto-selection would move the displayed price without moving the checked
    // radio — the page would show the 3-pack price and add the 1-pack to cart.
    const radios = productInfo.querySelectorAll(
      '.product-form__card-radio, .product-form__input--pill input[type="radio"]'
    );
    if (!radios.length) return false;

    const match = Array.from(radios).find((radio) => radio.value.toLowerCase().trim() === target);
    if (!match || match.disabled) return false;

    if (!match.checked) {
      match.checked = true;
      match.dispatchEvent(new Event('change', { bubbles: true }));
    }

    productInfo.querySelectorAll('[data-variant-card]').forEach((card) => {
      const radio = card.querySelector('.product-form__card-radio');
      card.classList.toggle('is-selected', radio === match);
    });

    return true;
  }

  function parseGalleryCaptionMap(productInfo) {
    const raw = productInfo.dataset.galleryCaptionMap;
    if (!raw) return null;
    const map = new Map();
    raw.split('||').forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const pipeIdx = trimmed.indexOf('|');
      if (pipeIdx === -1) return;
      const key = trimmed.slice(0, pipeIdx).trim().toLowerCase();
      const value = trimmed.slice(pipeIdx + 1).trim();
      if (key && value) map.set(key, value);
    });
    return map.size ? map : null;
  }

  function updateGalleryCaption(productInfo, variant) {
    const captionEl = productInfo.querySelector('[data-gallery-caption]');
    const map = parseGalleryCaptionMap(productInfo);
    if (!captionEl || !map) return;

    const packValue = variant?.options?.[0] || variant?.title || '';
    const caption = map.get(String(packValue).toLowerCase().trim());
    if (caption) {
      captionEl.textContent = caption;
      captionEl.hidden = false;
    } else {
      captionEl.textContent = '';
      captionEl.hidden = true;
    }
  }


  function runMostPopularSelection(productInfo) {
    if (selectMostPopularVariant(productInfo)) return;
    syncCardSelection(productInfo);
  }

  function reorderPackSizeCards(productInfo) {
    const packOrder = ['3pc set', '6pc set', '10pc set'];

    productInfo.querySelectorAll('.product-form__card-list').forEach((list) => {
      const cards = Array.from(list.querySelectorAll('[data-variant-card]'));
      const cardsByValue = new Map();

      cards.forEach((card) => {
        const radio = card.querySelector('.product-form__card-radio');
        if (!radio) return;
        cardsByValue.set(radio.value.toLowerCase().trim(), card);
      });

      if (!packOrder.every((value) => cardsByValue.has(value))) return;
      packOrder.forEach((value) => list.appendChild(cardsByValue.get(value)));
    });
  }

  function init(productInfo) {
    if (productInfo.dataset.heroInitialized === 'true') return;
    productInfo.dataset.heroInitialized = 'true';

    const gallery = productInfo.querySelector('media-gallery');
    if (gallery) {
      setupDesktopNav(gallery);
      updateSliderCounter(gallery);
    }

    const captionMap = parseGalleryCaptionMap(productInfo);
    if (captionMap) {
      updateGalleryCaption(productInfo, getSelectedVariantFromPicker(productInfo));
    }

    const bootMostPopular = () => {
      reorderPackSizeCards(productInfo);
      runMostPopularSelection(productInfo);
    };

    bootMostPopular();
    if (customElements.get('variant-selects')) {
      requestAnimationFrame(bootMostPopular);
    } else {
      customElements.whenDefined('variant-selects').then(bootMostPopular);
    }
    document.addEventListener('DOMContentLoaded', bootMostPopular, { once: true });

    reorderMobileColorControls(productInfo);

    const inputs = getColorInputs(productInfo);
    const colorOrder = inputs ? inputs.map(i => i.value.toUpperCase()) : null;

    if (gallery && colorOrder) {
      sortGalleryByColor(gallery, colorOrder);
      const initialActive = getActiveItem(gallery);
      if (initialActive) centerThumbnailFor(gallery, initialActive.dataset.mediaId);
      updateSliderCounter(gallery);
    }
    if (inputs) updateColorLegend(productInfo);

    desktopMQL.addEventListener('change', () => {
      reorderMobileColorControls(productInfo);
    });

    if (typeof window.subscribe === 'function') {
      window.subscribe('variant-change', (payload) => {
        const sectionId = productInfo.dataset.section;
        if (payload?.data?.sectionId !== sectionId) return;
        const variant = payload?.data?.variant;
        if (variant) updateGalleryCaption(productInfo, variant);
        requestAnimationFrame(() => {
          if (gallery && colorOrder) {
            sortGalleryByColor(gallery, colorOrder);
            const active = getActiveItem(gallery);
            if (active) {
              centerThumbnailFor(gallery, active.dataset.mediaId);
              const rect = active.getBoundingClientRect();
              if (rect.top < 0 || rect.bottom > window.innerHeight) {
                window.scrollTo({ top: rect.top + window.scrollY - 80, behavior: 'smooth' });
              }
            }
            updateSliderCounter(gallery);
          }
          if (inputs) updateColorLegend(productInfo);
          reorderPackSizeCards(productInfo);
          syncCardSelection(productInfo);
          reorderMobileColorControls(productInfo);
        });
      });
    }
  }

  function bootAllProductHeroInstances() {
    document.querySelectorAll('[id^="ProductInfo-"]').forEach(init);
  }

  bootAllProductHeroInstances();
  document.addEventListener('DOMContentLoaded', bootAllProductHeroInstances);
})();

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-scroll-to-reviews]');
  if (!trigger) return;

  const href = trigger.getAttribute('href');
  let target = null;
  if (href && href.charAt(0) === '#' && href.length > 1) {
    target = document.querySelector(href);
  }
  if (!target) {
    target = document.querySelector('[data-customer-reviews-section]');
  }

  const reviewsTab = document.querySelector('[data-tab-target="reviews"]');
  if (reviewsTab) reviewsTab.click();

  const scrollTarget = target || reviewsTab;
  if (!scrollTarget) return;

  e.preventDefault();
  scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

document.addEventListener('click', (e) => {
  const summary = e.target.closest('.product-hero__accordion-summary');
  if (!summary) return;
  const details = summary.parentElement;
  if (!details || details.tagName !== 'DETAILS') return;
  const wrapper = details.querySelector('.product-hero__accordion-content-wrapper');
  if (!wrapper) return;

  e.preventDefault();

  if (details.open) {
    details.classList.add('is-closing');
    wrapper.addEventListener('transitionend', function handler(ev) {
      if (ev.propertyName !== 'grid-template-rows') return;
      wrapper.removeEventListener('transitionend', handler);
      details.classList.remove('is-closing');
      details.open = false;
    });
  } else {
    details.classList.add('is-opening');
    details.open = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        details.classList.remove('is-opening');
      });
    });
  }
});
